import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Divider from '@material-ui/core/Divider'
import InputLabel from '@material-ui/core/InputLabel'
import FormControl from '@material-ui/core/FormControl'
import Chip from '@material-ui/core/Chip'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import RadioGroup from '@material-ui/core/RadioGroup'
import Radio from '@material-ui/core/Radio'
import Collapse from '@material-ui/core/Collapse'
import AdminAPI from 'config/api/AdminAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'

import componentStyles from 'assets/theme/components/cards/cards/card-button.js'

const useStyles = makeStyles(componentStyles)

const COVER_SOURCES = ['cover.lifecompare.ie', 'cover.insureyourincome.ie']

// Spread first so fields this screen doesn't manage are saved back unchanged.
function withToggleDefaults(data) {
    return {
        ...data,
        ageRejection: { underMin: false, overMax: false, ...(data.ageRejection || {}) },
        coverAllocation: { enabled: true, ...(data.coverAllocation || {}) },
        queue: { enabled: true, ...(data.queue || {}) }
    }
}

function AutoAllocationRules(props) {
    const classes = useStyles()
    const api = new AdminAPI()
    const [loading, setLoading] = useState(true)
    const [selectedProduct, setSelectedProduct] = useState('life-insurance')
    const [rules, setRules] = useState(null)
    const [lifeRules, setLifeRules] = useState(null)
    const [ipRules, setIpRules] = useState(null)

    useEffect(() => {
        getRules()
    }, [])

    useEffect(() => {
        // Switch rules when product changes
        if (selectedProduct === 'life-insurance' && lifeRules) {
            setRules(lifeRules)
        } else if (selectedProduct === 'income-protection' && ipRules) {
            setRules(ipRules)
        }
    }, [selectedProduct, lifeRules, ipRules])

    async function getRules() {
        try {
            const lifeData = await api.getLifeAllocationRules()
            console.log('Fetched life rules:', lifeData)
            const safeLifeRules = withToggleDefaults({
                ...lifeData,
                enabled: lifeData.enabled !== undefined ? lifeData.enabled : true,
                businessHours: lifeData.businessHours || { start: 9, end: 17 },
                ageRange: lifeData.ageRange || { min: 19, max: 64 },
                largeBuyers: lifeData.largeBuyers || [],
                largeBuyerStrategy: lifeData.largeBuyerStrategy || 'priority',
                dailyLimits: lifeData.dailyLimits || [],
                defaultDailyLimit: lifeData.defaultDailyLimit || 1000,
                rateLimitHours: lifeData.rateLimitHours || 2,
                weekendAllocation: lifeData.weekendAllocation !== undefined ? lifeData.weekendAllocation : false
            })
            setLifeRules(safeLifeRules)

            const ipData = await api.getIncomeProtectionAllocationRules()
            console.log('Fetched IP rules:', ipData)
            const safeIpRules = withToggleDefaults({
                ...ipData,
                enabled: ipData.enabled !== undefined ? ipData.enabled : true,
                businessHours: ipData.businessHours || { start: 8, end: 19, endMinutes: 30 },
                ageRange: ipData.ageRange || { min: 19, max: 55 },
                largeBuyers: ipData.largeBuyers || [],
                largeBuyerStrategy: ipData.largeBuyerStrategy || 'priority',
                dailyLimits: ipData.dailyLimits || [],
                defaultDailyLimit: ipData.defaultDailyLimit || 1000,
                rateLimitHours: ipData.rateLimitHours || 2,
                weekendAllocation: ipData.weekendAllocation !== undefined ? ipData.weekendAllocation : false,
                allowedClasses: ipData.allowedClasses || ['Class 1', 'Class 2', 'Class 3'],
                minimumSalary: ipData.minimumSalary || 40000,
                priorityBuyerRules: ipData.priorityBuyerRules || []
            })
            setIpRules(safeIpRules)

            // Set initial rules based on selected product
            setRules(safeLifeRules)
            setLoading(false)
        } catch (e) {
            onError(e)
            setLoading(false)
        }
    }

    async function saveRules() {
        try {
            if (selectedProduct === 'life-insurance') {
                await api.updateLifeAllocationRules(rules)
                setLifeRules(rules)
            } else {
                await api.updateIncomeProtectionAllocationRules(rules)
                setIpRules(rules)
            }
            if (props.onRulesSaved) {
                props.onRulesSaved(selectedProduct, rules)
            }
            NotificationManager.success('Rules updated successfully')
        } catch (e) {
            onError(e)
        }
    }

    const handleBusinessHoursChange = (prop) => (event) => {
        setRules({
            ...rules,
            businessHours: { ...rules.businessHours, [prop]: parseInt(event.target.value) }
        })
    }

    const handleAgeRangeChange = (prop) => (event) => {
        setRules({
            ...rules,
            ageRange: { ...rules.ageRange, [prop]: parseInt(event.target.value) }
        })
    }

    const handleToggleGroupChange = (group, prop) => (event) => {
        setRules({
            ...rules,
            [group]: { ...rules[group], [prop]: event.target.checked }
        })
    }

    const companyName = (companyId) => {
        const company = (props.companies || []).find(c => c.companyId === companyId)
        return company ? company.name : companyId.toUpperCase()
    }

    const handleLargeBuyersChange = (event) => {
        setRules({ ...rules, largeBuyers: event.target.value })
    }

    const moveLargeBuyer = (index, direction) => {
        const newOrder = [...rules.largeBuyers]
        const target = index + direction
        if (target < 0 || target >= newOrder.length) return
        ;[newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]]
        setRules({ ...rules, largeBuyers: newOrder })
    }

    const handleStrategyChange = (event) => {
        setRules({ ...rules, largeBuyerStrategy: event.target.value })
    }

    const ordinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd']
        const v = n % 100
        return n + (s[(v - 20) % 10] || s[v] || s[0])
    }

    const handleDailyLimitChange = (index, prop) => (event) => {
        const newLimits = [...rules.dailyLimits]
        newLimits[index][prop] = parseInt(event.target.value)
        setRules({ ...rules, dailyLimits: newLimits })
    }

    const addDailyLimit = () => {
        setRules({
            ...rules,
            dailyLimits: [...rules.dailyLimits, { allocationLimit: 0, dailyLimit: 0 }]
        })
    }

    const removeDailyLimit = (index) => {
        const newLimits = [...rules.dailyLimits]
        newLimits.splice(index, 1)
        setRules({ ...rules, dailyLimits: newLimits })
    }

    // Priority Buyer Rules handlers
    const addPriorityBuyerRule = () => {
        const newRule = {
            companyId: '',
            companyName: '',
            priority: (rules.priorityBuyerRules?.length || 0) + 1,
            criteria: {
                allowedClasses: ['Class 1', 'Class 2'],
                minimumSalary: 45000,
                maxAge: 0
            },
            enabled: true
        }
        setRules({
            ...rules,
            priorityBuyerRules: [...(rules.priorityBuyerRules || []), newRule]
        })
    }

    const removePriorityBuyerRule = (index) => {
        const newRules = [...rules.priorityBuyerRules]
        newRules.splice(index, 1)
        // Re-number priorities
        newRules.forEach((rule, i) => rule.priority = i + 1)
        setRules({ ...rules, priorityBuyerRules: newRules })
    }

    const movePriorityBuyerRule = (index, direction) => {
        const newRules = [...rules.priorityBuyerRules]
        const target = index + direction
        if (target < 0 || target >= newRules.length) return
        ;[newRules[index], newRules[target]] = [newRules[target], newRules[index]]
        // Re-number priorities to reflect the new order
        newRules.forEach((rule, i) => rule.priority = i + 1)
        setRules({ ...rules, priorityBuyerRules: newRules })
    }

    const handlePriorityBuyerChange = (index, field, value) => {
        const newRules = [...rules.priorityBuyerRules]
        if (field === 'companyId') {
            const company = props.companies.find(c => c.companyId === value)
            newRules[index].companyId = value
            newRules[index].companyName = company ? company.name : value
        } else if (field.startsWith('criteria.')) {
            const criteriaField = field.split('.')[1]
            newRules[index].criteria[criteriaField] = value
        } else {
            newRules[index][field] = value
        }
        setRules({ ...rules, priorityBuyerRules: newRules })
    }

    const hours = Array.from({length: 24}, (_, i) => i);
    
    // Generate time slots with 30-minute increments for more granular control
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
        timeSlots.push({ hour, minutes: 0, label: `${hour.toString().padStart(2, '0')}:00` });
        timeSlots.push({ hour, minutes: 30, label: `${hour.toString().padStart(2, '0')}:30` });
    }

    const handleEndTimeChange = (event) => {
        const selectedSlot = timeSlots.find(slot => slot.label === event.target.value);
        if (selectedSlot) {
            setRules({
                ...rules,
                businessHours: { 
                    ...rules.businessHours, 
                    end: selectedSlot.hour,
                    endMinutes: selectedSlot.minutes
                }
            });
        }
    };

    const getEndTimeValue = () => {
        const hour = rules.businessHours.end;
        const minutes = rules.businessHours.endMinutes || 0;
        return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    return (
        <Card classes={{ root: classes.cardRoot }}>
            <CardHeader
                subheader={
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Box component={Typography} variant='h3' marginBottom='0!important'>
                                Auto Allocation Rules
                            </Box>
                        </Grid>
                        <Grid item style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={rules?.enabled !== false}
                                        onChange={(e) => setRules({ ...rules, enabled: e.target.checked })}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" style={{ fontWeight: 500, marginLeft: '0.5rem' }}>
                                        {rules?.enabled !== false ? 'Enabled' : 'Disabled'}
                                    </Typography>
                                }
                                style={{ marginRight: '1.5rem' }}
                            />
                            <FormControl variant="outlined" size="small" style={{minWidth: 200}}>
                                <InputLabel id="product-select-label">Product</InputLabel>
                                <Select
                                    labelId="product-select-label"
                                    value={selectedProduct}
                                    onChange={(e) => setSelectedProduct(e.target.value)}
                                    label="Product"
                                >
                                    <MenuItem value="life-insurance">Life Insurance</MenuItem>
                                    <MenuItem value="income-protection">Income Protection</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                }
                classes={{ root: classes.cardHeaderRoot }}
            />
            <CardContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <Loader />
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {/* Always applies — visible even when auto allocation is disabled */}
                        <Grid item xs={12}>
                            <Box mb={2}>
                                <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                    Age Rules (always apply)
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    The age range decides who can be auto-allocated. The reject options below run on every new lead, even when auto allocation is disabled.
                                </Typography>
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Lead Age Criteria</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <TextField
                                            label="Minimum Age"
                                            type="number"
                                            variant="outlined"
                                            value={rules.ageRange.min}
                                            onChange={handleAgeRangeChange('min')}
                                            style={{ marginRight: '1rem' }}
                                        />
                                        <TextField
                                            label="Maximum Age"
                                            type="number"
                                            variant="outlined"
                                            value={rules.ageRange.max}
                                            onChange={handleAgeRangeChange('max')}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Auto-reject Outside Age Range</Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="column" mb={1}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={rules.ageRejection.underMin === true}
                                                    onChange={handleToggleGroupChange('ageRejection', 'underMin')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography variant="body2" color="textSecondary" style={{ marginLeft: '0.5rem' }}>Reject leads under {rules.ageRange.min}</Typography>}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={rules.ageRejection.overMax === true}
                                                    onChange={handleToggleGroupChange('ageRejection', 'overMax')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography variant="body2" color="textSecondary" style={{ marginLeft: '0.5rem' }}>Reject leads over {rules.ageRange.max}</Typography>}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        When off, out-of-range leads stay unallocated for manual review. When on, they are moved to Rejected with a reason like &ldquo;Age 67 over maximum ({rules.ageRange.max}) &ndash; auto rejected&rdquo;. Leads with a missing or unreadable date of birth are never auto-rejected.
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Disabled message */}
                        {rules?.enabled === false && (
                            <Grid item xs={12}>
                                <Box 
                                    p={3} 
                                    bgcolor="#fff3cd" 
                                    borderRadius={4}
                                    border="1px solid #ffc107"
                                    textAlign="center"
                                >
                                    <Typography variant="body1" style={{ color: '#856404' }}>
                                        Auto allocation is currently <strong>disabled</strong> for {selectedProduct === 'life-insurance' ? 'Life Insurance' : 'Income Protection'}. 
                                        No leads are auto-allocated or queued, including cover site leads, and the queue is not released. Toggle the switch above to enable and configure the rules.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        
                        <Collapse in={rules?.enabled !== false} style={{ width: '100%' }}>
                        <Grid container spacing={4} style={{ padding: '0 16px' }}>
                        {/* Cover site leads */}
                        <Grid item xs={12}>
                            <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                        Cover Site Leads
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Leads from {COVER_SOURCES.join(' and ')}.
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={rules.coverAllocation.enabled !== false}
                                            onChange={handleToggleGroupChange('coverAllocation', 'enabled')}
                                            color="primary"
                                        />
                                    }
                                    label={<Typography variant="body2" style={{ fontWeight: 500, marginLeft: '0.5rem' }}>{rules.coverAllocation.enabled !== false ? 'Enabled' : 'Disabled'}</Typography>}
                                />
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            <Box p={2} bgcolor="#e3f2fd" borderRadius={4} border="1px solid #64b5f6">
                                <Typography variant="body2" style={{ color: '#1565c0' }} component="div">
                                    <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                                        <li><strong>Actual Insurances is the only buyer</strong> cover site leads are auto-allocated to.</li>
                                        <li>If Actual Insurances already has that phone number, the lead goes to the next large buyer in order, skipping Actual Insurances{rules.largeBuyers.filter(id => id !== 'ai').length > 0 ? ` (${rules.largeBuyers.filter(id => id !== 'ai').map(companyName).join(' → ')})` : ''}.</li>
                                        <li>If Actual Insurances is at its weekly cap, the lead is queued for them {rules.queue.enabled !== false ? '(queue is on)' : '— the queue is off, so it is left unallocated'}.</li>
                                        <li>Only the {selectedProduct === 'life-insurance' ? 'age range' : 'risk class, salary and age range'} rules apply. Business hours, weekend allocation, daily limits and rate limits <strong>do not apply</strong> to cover site leads.</li>
                                        <li>When disabled, cover site leads are left unallocated for manual review.</li>
                                    </ul>
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Lead queue */}
                        <Grid item xs={12}>
                            <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                        Lead Queue
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Holds eligible leads when every buyer is at their weekly cap, and releases them {selectedProduct === 'life-insurance' ? 'on Sunday, 6pm–10pm' : 'on Monday, 8am–12pm'}.
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={rules.queue.enabled !== false}
                                            onChange={handleToggleGroupChange('queue', 'enabled')}
                                            color="primary"
                                        />
                                    }
                                    label={<Typography variant="body2" style={{ fontWeight: 500, marginLeft: '0.5rem' }}>{rules.queue.enabled !== false ? 'Enabled' : 'Disabled'}</Typography>}
                                />
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            <Typography variant="caption" color="textSecondary">
                                When disabled, leads that can&apos;t be placed are left unallocated instead of queued, and the {selectedProduct === 'life-insurance' ? 'Sunday' : 'Monday'} release does not run. Leads already queued keep their &ldquo;Queued&rdquo; badge and can be allocated or rejected manually.
                            </Typography>
                        </Grid>

                        {/* 1. Schedule & Eligibility */}
                        <Grid item xs={12}>
                            <Box mb={2}>
                                <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                    1. Schedule & Eligibility
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Control when the auto-allocation runs and who qualifies.
                                </Typography>
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Business Hours (Mon-Fri)</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <FormControl variant="outlined" style={{ marginRight: '1rem', minWidth: '120px' }}>
                                            <InputLabel>Start Time</InputLabel>
                                            <Select
                                                value={rules.businessHours.start}
                                                onChange={handleBusinessHoursChange('start')}
                                                label="Start Time"
                                            >
                                                {hours.map(hour => (
                                                    <MenuItem key={hour} value={hour}>{hour.toString().padStart(2, '0')}:00</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Typography variant="body2" color="textSecondary" style={{ marginRight: '1rem' }}>to</Typography>
                                        <FormControl variant="outlined" style={{ minWidth: '120px' }}>
                                            <InputLabel>End Time</InputLabel>
                                            <Select
                                                value={getEndTimeValue()}
                                                onChange={handleEndTimeChange}
                                                label="End Time"
                                            >
                                                {timeSlots.map(slot => (
                                                    <MenuItem key={slot.label} value={slot.label}>{slot.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Leads received outside these hours are routed to Large Buyers.
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Weekend Allocation</Typography>
                                    </Box>
                                    <Box mb={1}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={rules.weekendAllocation}
                                                    onChange={(e) => setRules({ ...rules, weekendAllocation: e.target.checked })}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography variant="body2" color="textSecondary" style={{ marginLeft: '0.5rem' }}>{rules.weekendAllocation ? "Enabled" : "Disabled"}</Typography>}
                                            labelPlacement="end"
                                        />
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        If disabled, weekend leads remain unassigned.
                                    </Typography>
                                </Grid>

                                {/* Income Protection specific rules */}
                                {selectedProduct === 'income-protection' && (
                                    <>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Allowed Risk Classes</Typography>
                                            </Box>
                                            <FormControl fullWidth variant="outlined">
                                                <InputLabel>Select Classes</InputLabel>
                                                <Select
                                                    multiple
                                                    value={rules.allowedClasses || ['Class 1', 'Class 2', 'Class 3']}
                                                    onChange={(e) => setRules({ ...rules, allowedClasses: e.target.value })}
                                                    label="Select Classes"
                                                    renderValue={(selected) => (
                                                        <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                            {selected.map((value) => (
                                                                <Chip key={value} label={value} style={{margin: '2px'}} />
                                                            ))}
                                                        </Box>
                                                    )}
                                                >
                                                    <MenuItem value="Class 1">Class 1</MenuItem>
                                                    <MenuItem value="Class 2">Class 2</MenuItem>
                                                    <MenuItem value="Class 3">Class 3</MenuItem>
                                                    <MenuItem value="Class 4">Class 4</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <Box mt={1}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Leads with risk classes not selected will not be auto-allocated.
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Minimum Salary</Typography>
                                            </Box>
                                            <TextField
                                                label="Minimum Salary (€)"
                                                type="number"
                                                variant="outlined"
                                                fullWidth
                                                value={rules.minimumSalary || 40000}
                                                onChange={(e) => setRules({ ...rules, minimumSalary: parseInt(e.target.value) })}
                                            />
                                            <Box mt={1}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Leads with salary below this amount will not be auto-allocated.
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </>
                                )}

                                {/* Info note for Income Protection */}
                                {selectedProduct === 'income-protection' && (
                                    <Grid item xs={12}>
                                        <Box 
                                            p={2} 
                                            bgcolor="#e3f2fd" 
                                            borderRadius={4}
                                            border="1px solid #64b5f6"
                                            display="flex"
                                            alignItems="center"
                                        >
                                            <Typography variant="body2" style={{ color: '#1565c0' }}>
                                                Leads with risk level "Declined" or salary below €30,000 are automatically rejected
                                            </Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {/* 2. Buyer Groups */}
                        <Grid item xs={12}>
                            <Box mb={2}>
                                <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                    2. Buyer Groups
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Define which companies are considered "Large Buyers" for off-hours rotation.
                                </Typography>
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Large Buyers List</Typography>
                                    </Box>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>Select Companies</InputLabel>
                                        <Select
                                            multiple
                                            value={rules.largeBuyers}
                                            onChange={handleLargeBuyersChange}
                                            label="Select Companies"
                                            renderValue={(selected) => (
                                                <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
                                                    {selected.map((value) => {
                                                        const company = props.companies.find(c => c.companyId === value)
                                                        return <Chip key={value} label={company ? company.name : value} style={{margin: '2px'}} />
                                                    })}
                                                </Box>
                                            )}
                                        >
                                            {props.companies.map((company) => (
                                                <MenuItem key={company.companyId} value={company.companyId}>
                                                    {company.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Box mt={3} mb={1}>
                                        <Typography variant="subtitle2" style={{ marginBottom: '0.5rem' }}>Allocation Strategy</Typography>
                                        <RadioGroup
                                            row
                                            value={rules.largeBuyerStrategy || 'priority'}
                                            onChange={handleStrategyChange}
                                        >
                                            <FormControlLabel
                                                value="priority"
                                                control={<Radio color="primary" />}
                                                label={<Typography variant="body2">Priority order (always try buyers in the order below)</Typography>}
                                            />
                                            <FormControlLabel
                                                value="rotation"
                                                control={<Radio color="primary" />}
                                                label={<Typography variant="body2">Round-robin (rotate through buyers each lead)</Typography>}
                                            />
                                        </RadioGroup>
                                    </Box>

                                    {rules.largeBuyers && rules.largeBuyers.length > 0 && (
                                        <Box mt={2} mb={1}>
                                            <Typography variant="subtitle2" style={{ marginBottom: '0.5rem' }}>Allocation Order</Typography>
                                            <Box style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                                                {rules.largeBuyers.map((companyId, index) => {
                                                    const company = props.companies.find(c => c.companyId === companyId)
                                                    const isPriority = (rules.largeBuyerStrategy || 'priority') === 'priority'
                                                    return (
                                                        <Box
                                                            key={companyId}
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            px={2}
                                                            py={1}
                                                            style={{ borderBottom: index < rules.largeBuyers.length - 1 ? '1px solid #f0f0f0' : 'none', opacity: isPriority ? 1 : 0.6 }}
                                                        >
                                                            <Box display="flex" alignItems="center">
                                                                <Typography variant="body2" color="textSecondary" style={{ width: 40 }}>{ordinal(index + 1)}</Typography>
                                                                <Typography variant="body2">{company ? company.name : companyId}</Typography>
                                                            </Box>
                                                            <Box>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={!isPriority || index === 0}
                                                                    onClick={() => moveLargeBuyer(index, -1)}
                                                                >
                                                                    <ArrowUpwardIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={!isPriority || index === rules.largeBuyers.length - 1}
                                                                    onClick={() => moveLargeBuyer(index, 1)}
                                                                >
                                                                    <ArrowDownwardIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    )
                                                })}
                                            </Box>
                                        </Box>
                                    )}

                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            {(rules.largeBuyerStrategy || 'priority') === 'priority'
                                                ? 'Off-hours leads are attempted in the order above. If a buyer is full or rejects the lead, the next is tried. If all fail, the lead remains unallocated.'
                                                : 'Off-hours leads rotate through the buyers above. Order is ignored in round-robin mode.'}
                                        </Typography>
                                    </Box>
                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            All other companies with active {selectedProduct === 'life-insurance' ? 'Life Insurance' : 'Income Protection'} deliveries are automatically treated as "Small Buyers."
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* 3. Small Buyer Constraints */}
                        <Grid item xs={12}>
                            <Box mb={2}>
                                <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                    3. Small Buyer Constraints (Business Hours)
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Rules applied to Small Buyers to prevent over-allocation.
                                </Typography>
                            </Box>
                            <Divider style={{marginBottom: '1.5rem'}} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Rate Limiting</Typography>
                                    </Box>
                                    <TextField
                                        label="Minimum Hours Between Leads"
                                        type="number"
                                        variant="outlined"
                                        fullWidth
                                        value={rules.rateLimitHours}
                                        onChange={(e) => setRules({ ...rules, rateLimitHours: parseInt(e.target.value) })}
                                    />
                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            A specific company cannot receive another lead until this time has passed.
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box>
                                        <Typography variant="subtitle2" style={{ marginBottom: '1rem' }}>Daily Volume Caps</Typography>
                                    </Box>
                                    
                                    <Box mb={2}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={5}>
                                                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '0rem' }}>If Weekly Allocation is less than or equal to:</Typography>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '0rem' }}>Max Leads Per Day:</Typography>
                                            </Grid>
                                            <Grid item xs={2}></Grid>
                                        </Grid>
                                    </Box>

                                    {rules.dailyLimits.map((limit, index) => (
                                        <Box key={index} mb={2}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={5}>
                                                    <TextField
                                                        type="number"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={limit.allocationLimit}
                                                        onChange={handleDailyLimitChange(index, 'allocationLimit')}
                                                    />
                                                </Grid>
                                                <Grid item xs={5}>
                                                    <TextField
                                                        type="number"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        value={limit.dailyLimit}
                                                        onChange={handleDailyLimitChange(index, 'dailyLimit')}
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <IconButton onClick={() => removeDailyLimit(index)} size="small">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}

                                    {/* Default Fallback Row */}
                                    <Box mb={2}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={5}>
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value="Unlimited"
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={rules.defaultDailyLimit}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={2}>
                                                <Typography variant="caption" color="textSecondary">Default</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={addDailyLimit}
                                        variant="outlined"
                                        size="small"
                                    >
                                        Add Tier
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* 4. Priority Buyer Rules (Income Protection only) */}
                        {selectedProduct === 'income-protection' && (
                            <Grid item xs={12}>
                                <Box mb={2}>
                                    <Typography variant="h5" color="textPrimary" gutterBottom style={{fontSize: '1.1rem'}}>
                                        4. Priority Buyer Rules
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Define buyers who get exclusive access to leads matching specific criteria. Priority buyers are removed from normal rotation and only receive leads that match their criteria.
                                    </Typography>
                                </Box>
                                <Divider style={{marginBottom: '1.5rem'}} />
                                
                                <Grid container spacing={3}>
                                    {(rules.priorityBuyerRules || []).map((rule, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Box 
                                                p={2} 
                                                border="1px solid #e0e0e0" 
                                                borderRadius={4}
                                                bgcolor={rule.enabled ? '#f5f5f5' : '#fafafa'}
                                            >
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12}>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                            <Typography variant="subtitle2">
                                                                Priority #{rule.priority}
                                                            </Typography>
                                                            <Box display="flex" alignItems="center">
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={index === 0}
                                                                    onClick={() => movePriorityBuyerRule(index, -1)}
                                                                >
                                                                    <ArrowUpwardIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={index === (rules.priorityBuyerRules || []).length - 1}
                                                                    onClick={() => movePriorityBuyerRule(index, 1)}
                                                                >
                                                                    <ArrowDownwardIcon fontSize="small" />
                                                                </IconButton>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Switch
                                                                            checked={rule.enabled}
                                                                            onChange={(e) => handlePriorityBuyerChange(index, 'enabled', e.target.checked)}
                                                                            color="primary"
                                                                            size="small"
                                                                        />
                                                                    }
                                                                    label={<Typography variant="caption">{rule.enabled ? 'Enabled' : 'Disabled'}</Typography>}
                                                                />
                                                                <IconButton onClick={() => removePriorityBuyerRule(index)} size="small">
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} md={3}>
                                                        <FormControl fullWidth variant="outlined" size="small">
                                                            <InputLabel>Company</InputLabel>
                                                            <Select
                                                                value={rule.companyId}
                                                                onChange={(e) => handlePriorityBuyerChange(index, 'companyId', e.target.value)}
                                                                label="Company"
                                                            >
                                                                {props.companies.map((company) => (
                                                                    <MenuItem key={company.companyId} value={company.companyId}>
                                                                        {company.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} md={3}>
                                                        <FormControl fullWidth variant="outlined" size="small">
                                                            <InputLabel>Allowed Classes</InputLabel>
                                                            <Select
                                                                multiple
                                                                value={rule.criteria?.allowedClasses || []}
                                                                onChange={(e) => handlePriorityBuyerChange(index, 'criteria.allowedClasses', e.target.value)}
                                                                label="Allowed Classes"
                                                                renderValue={(selected) => selected.join(', ')}
                                                            >
                                                                <MenuItem value="Class 1">Class 1</MenuItem>
                                                                <MenuItem value="Class 2">Class 2</MenuItem>
                                                                <MenuItem value="Class 3">Class 3</MenuItem>
                                                                <MenuItem value="Class 4">Class 4</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} md={3}>
                                                        <TextField
                                                            label="Minimum Salary (€)"
                                                            type="number"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            value={rule.criteria?.minimumSalary || 0}
                                                            onChange={(e) => handlePriorityBuyerChange(index, 'criteria.minimumSalary', parseInt(e.target.value))}
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12} md={3}>
                                                        <TextField
                                                            label="Max Age"
                                                            type="number"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                            value={rule.criteria?.maxAge || 0}
                                                            onChange={(e) => handlePriorityBuyerChange(index, 'criteria.maxAge', parseInt(e.target.value))}
                                                            helperText="0 = no age limit"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    ))}
                                    
                                    <Grid item xs={12}>
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={addPriorityBuyerRule}
                                            variant="outlined"
                                            size="small"
                                        >
                                            Add Priority Buyer Rule
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box 
                                            p={2} 
                                            bgcolor="#fff3e0" 
                                            borderRadius={4}
                                            border="1px solid #ffb74d"
                                        >
                                            <Typography variant="body2" style={{ color: '#e65100' }}>
                                                Priority buyers are excluded from normal rotation. They will ONLY receive leads that match their specific criteria. If no matching leads come in, they will receive no leads.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        </Grid>
                        </Collapse>

                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={saveRules}
                                    size="large"
                                >
                                    Save Rules
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
        </Card>
    )
}

export default AutoAllocationRules
